import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { router } from 'expo-router';
import { FileText, Plus } from 'lucide-react-native';
import { colors, formatMinutes, groupByRoom, pick, sumMinutes, tasksForDay, weekdayName, weekdayShort, type Weekday } from '@hogar/shared';
import { Button, Card, RowLink, Screen, SectionTitle, Txt, useT } from '../../components/ui';
import { roomIcons } from '../../components/icons';
import { useHouse } from '../../lib/house';
import { crewSlice, useCrew } from '../../lib/crew';
import { CrewSwitch } from '../../components/CrewSwitch';

const WEEK: Weekday[] = [0, 1, 2, 3, 4, 5, 6];

export default function Routines() {
  const { t, lang } = useT();
  const house = useHouse();
  const crew = useCrew();
  const { rooms, tasks, staff: crewStaff } = crewSlice(house, crew);
  const { today } = house;
  const [day, setDay] = useState<Weekday>(today.weekday);
  const dayTasks = tasksForDay(day, tasks);
  const groups = groupByRoom(dayTasks, rooms);
  const emptyRooms = rooms.filter((r) => !groups.some((g) => g.room.id === r.id));
  const n = dayTasks.length;

  return (
    <Screen title={t('routines')}>
      <CrewSwitch />
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 18 }}>
        {WEEK.map((d) => {
          const active = d === day;
          return (
            <Pressable
              key={d}
              onPress={() => setDay(d)}
              style={{ minWidth: 42, minHeight: 34, paddingHorizontal: 6, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? colors.primary : 'transparent' }}
            >
              <Txt size={13} weight={active ? 'semibold' : 'regular'} color={active ? colors.white : colors.textMuted}>
                {weekdayShort(d, lang)}
              </Txt>
            </Pressable>
          );
        })}
      </View>

      <Txt weight="semibold" size={22}>
        {weekdayName(day, lang)}
      </Txt>
      <Txt size={14} color={colors.textMuted} style={{ marginBottom: 12 }}>
        <Txt weight="semibold" size={14}>
          {formatMinutes(sumMinutes(dayTasks))}
        </Txt>{' '}
        · {n} {n === 1 ? t('task') : t('tasks')}
      </Txt>

      {groups.length === 0 ? (
        <Card style={{ paddingVertical: 24 }}>
          <Txt color={colors.textMuted} style={{ textAlign: 'center' }}>
            {t(crew === 'garden' ? 'dayOffGarden' : 'dayOff')}
          </Txt>
        </Card>
      ) : (
        <Card>
          {groups.map((g, i) => (
            <RowLink
              key={g.room.id}
              Icon={roomIcons[g.room.icon]}
              title={pick(g.room.name, lang)}
              subtitle={`${formatMinutes(g.minutes)} · ${g.tasks.length} ${g.tasks.length === 1 ? t('task') : t('tasks')}`}
              onPress={() => router.push({ pathname: '/room/[id]', params: { id: g.room.id, day: String(day) } })}
              last={i === groups.length - 1}
            />
          ))}
        </Card>
      )}

      {emptyRooms.length > 0 && (
        <>
        {groups.length > 0 && <SectionTitle>{t('otherRooms')}</SectionTitle>}
        <Card>
          {emptyRooms.map((r, i) => (
            <RowLink
              key={r.id}
              Icon={roomIcons[r.icon]}
              iconTint={colors.textFaint}
              title={pick(r.name, lang)}
              subtitle={t('otherDays', { n: tasks.filter((tk) => tk.roomId === r.id).length })}
              onPress={() => router.push({ pathname: '/room/[id]', params: { id: r.id, day: String(day) } })}
              last={i === emptyRooms.length - 1}
            />
          ))}
        </Card>
        </>
      )}

      {groups.length > 0 && (
        <Button label={t('viewPdf')} Icon={FileText} onPress={() => router.push({ pathname: '/checklist', params: { day: String(day), crew, lang: crewStaff[0]?.lang ?? 'es' } })} style={{ marginTop: 8 }} />
      )}
      <Button label={t(crew === 'garden' ? 'addArea' : 'addRoom')} Icon={Plus} variant="outline" onPress={() => router.push({ pathname: '/room-edit', params: { crew } })} style={{ marginTop: 10 }} />
    </Screen>
  );
}
